import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Star, Edit2, Image as ImageIcon, X, ExternalLink, Wifi, MapPin, Layers, Users, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import api, { BACKEND } from "@/lib/api";
import { AdminModal } from "@/components/AdminModal";
import { Header } from "./Schools";
import { ImageUpload } from "./Content";

const MODES = ["Online", "Offline", "Hybrid", "On-site", "Blended"];

const MODE_COLORS = {
  Online:   "bg-sky-50 text-sky-700 border-sky-200",
  Offline:  "bg-orange-50 text-orange-700 border-orange-200",
  Hybrid:   "bg-violet-50 text-violet-700 border-violet-200",
  "On-site":"bg-amber-50 text-amber-700 border-amber-200",
  Blended:  "bg-teal-50 text-teal-700 border-teal-200",
};

const MODE_ICON = { Online: Wifi, Offline: MapPin, Hybrid: Layers, "On-site": MapPin, Blended: Layers };
const modeIcon = (m) => MODE_ICON[m] || Users;

const EMPTY_FORM = {
  title: "", subtitle: "", description: "",
  category: "Pedagogy", duration_hours: 8, level: "Intermediate",
  cover_image: "",
  mode: "Online",
  is_published: true,
  training_start_time: "", training_end_time: "",
  timezone: "IST", venue: "",
};

const SI = "px-3 py-2.5 rounded-lg border border-gray-200 outline-none text-sm w-full focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10";

const resolveImg = (url) =>
  url ? (url.startsWith("http") ? url : `${BACKEND}${url}`) : null;

export default function Programs() {
  const [items, setItems]     = useState([]);
  const [modal, setModal]     = useState(null);   // "add" | "edit"
  const [editing, setEditing] = useState(null);
  const [f, setF]             = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [toggling, setToggling] = useState(null); // id being toggled

  const load = () => api.get("/programs").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setF(EMPTY_FORM); setEditing(null); setModal("add"); };

  const openEdit = (p) => {
    setF({
      title:               p.title               || "",
      subtitle:            p.subtitle            || "",
      description:         p.description         || "",
      category:            p.category            || "Pedagogy",
      duration_hours:      p.duration_hours      ?? 8,
      level:               p.level               || "Intermediate",
      cover_image:         p.cover_image         || "",
      mode:                p.mode                || "Online",
      is_published:        p.is_published        !== false,
      training_start_time: p.training_start_time || "",
      training_end_time:   p.training_end_time   || "",
      timezone:            p.timezone            || "IST",
      venue:               p.venue               || "",
    });
    setEditing(p);
    setModal("edit");
  };

  const closeModal = () => { setModal(null); setEditing(null); };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required."); return; }
    setSaving(true);
    try {
      const payload = { ...f, duration_hours: parseInt(f.duration_hours) || 8 };
      if (modal === "add") {
        await api.post("/programs", payload);
        toast.success("Program added.");
      } else {
        await api.put(`/programs/${editing.id}`, { ...editing, ...payload });
        toast.success("Program saved.");
      }
      closeModal();
      load();
    } catch { toast.error("Save failed. Please try again."); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this program?")) return;
    await api.delete(`/programs/${id}`);
    toast.success("Program deleted.");
    load();
  };

  const toggleFeatured = async (p) => {
    try {
      await api.put(`/programs/${p.id}`, { ...p, featured: !p.featured });
      load();
    } catch { toast.error("Failed to update."); }
  };

  // ── Quick mode cycle: Online → Offline → Hybrid → Online …
  const cycleMode = async (p) => {
    const next = MODES[(MODES.indexOf(p.mode || "Online") + 1) % MODES.length];
    setToggling(p.id);
    try {
      await api.put(`/programs/${p.id}`, { ...p, mode: next });
      setItems((prev) => prev.map((x) => x.id === p.id ? { ...x, mode: next } : x));
      toast.success(`Mode set to ${next}.`);
    } catch { toast.error("Failed to update mode."); }
    setToggling(null);
  };

  // ── Quick publish/draft toggle
  const togglePublished = async (p) => {
    const next = !p.is_published;
    setToggling(p.id);
    try {
      await api.put(`/programs/${p.id}`, { ...p, is_published: next });
      setItems((prev) => prev.map((x) => x.id === p.id ? { ...x, is_published: next } : x));
      toast.success(next ? "Program published." : "Program set to draft.");
    } catch { toast.error("Failed to update."); }
    setToggling(null);
  };

  const imgSrc = resolveImg(f.cover_image);

  return (
    <div className="fade-up">
      <Header
        title="Programs"
        subtitle="Manage your training catalogue."
        actions={
          <button onClick={openAdd} data-testid="add-program"
            className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b]">
            <Plus className="w-4 h-4" /> New program
          </button>
        }
      />

      {/* ── Program grid ── */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => {
          const src    = resolveImg(p.cover_image);
          const mode   = p.mode || "Online";
          const MIcon  = modeIcon(mode);
          const mColor = MODE_COLORS[mode] || "bg-gray-100 text-gray-600 border-gray-200";
          const busy   = toggling === p.id;

          return (
            <div key={p.id} className="rounded-2xl border border-gray-200 bg-white overflow-hidden group flex flex-col">

              {/* Cover image */}
              <div className="relative w-full aspect-[16/10] bg-gray-100 overflow-hidden shrink-0">
                {src ? (
                  <img src={src} alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  </div>
                )}

                {/* Status badges — top-left */}
                <div className="absolute top-2 left-2 flex gap-1.5">
                  {/* Published / Draft pill */}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                    p.is_published !== false
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-gray-100 text-gray-500 border-gray-200"
                  }`}>
                    {p.is_published !== false ? "Live" : "Draft"}
                  </span>
                  {/* Mode badge */}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${mColor}`}>
                    <MIcon className="w-2.5 h-2.5" />
                    {mode}
                  </span>
                </div>

                {/* Edit overlay button — top-right */}
                <button
                  onClick={() => openEdit(p)}
                  data-testid={`edit-program-${p.id}`}
                  className="absolute top-2 right-2 w-8 h-8 rounded-lg bg-white/90 backdrop-blur-sm grid place-items-center text-gray-600 hover:text-[#4F46E5] opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                  title="Edit program"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Card body */}
              <div className="p-4 flex flex-col flex-1">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-[#4F46E5]">{p.category}</div>
                <div className="font-medium mt-1 text-[#0F172A] leading-snug">{p.title}</div>
                {p.subtitle && <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{p.subtitle}</div>}
                <div className="text-xs text-gray-400 mt-1">{p.duration_hours}h · {p.level}</div>

                {/* Quick-action row */}
                <div className="mt-auto pt-3 flex items-center gap-2 flex-wrap">

                  {/* Publish/Draft toggle */}
                  <button
                    onClick={() => togglePublished(p)}
                    disabled={busy}
                    data-testid={`toggle-published-${p.id}`}
                    title={p.is_published !== false ? "Set to Draft" : "Publish"}
                    className={`text-xs flex items-center gap-1 transition-colors disabled:opacity-40 ${
                      p.is_published !== false
                        ? "text-emerald-600 hover:text-emerald-800"
                        : "text-gray-400 hover:text-emerald-600"
                    }`}
                  >
                    {p.is_published !== false
                      ? <><Eye className="w-3 h-3" /> Live</>
                      : <><EyeOff className="w-3 h-3" /> Draft</>
                    }
                  </button>

                  {/* Mode cycle */}
                  <button
                    onClick={() => cycleMode(p)}
                    disabled={busy}
                    data-testid={`cycle-mode-${p.id}`}
                    title="Click to cycle mode"
                    className={`text-xs flex items-center gap-1 transition-colors disabled:opacity-40 border rounded-full px-2 py-0.5 ${mColor}`}
                  >
                    <MIcon className="w-2.5 h-2.5" />
                    {mode}
                  </button>

                  {/* Featured */}
                  <button
                    onClick={() => toggleFeatured(p)}
                    data-testid={`toggle-featured-${p.id}`}
                    className={`text-xs flex items-center gap-1 ${p.featured ? "text-amber-600" : "text-gray-400 hover:text-gray-600"}`}
                  >
                    <Star className={`w-3 h-3 ${p.featured ? "fill-amber-500" : ""}`} />
                    {p.featured ? "Featured" : "Feature"}
                  </button>

                  {/* Manage full editor */}
                  <Link to={`/admin/programs/${p.id}`} className="text-xs text-[#4F46E5] hover:underline flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Manage
                  </Link>

                  {/* Delete */}
                  <button onClick={() => del(p.id)} className="text-xs text-red-500 hover:underline flex items-center gap-1 ml-auto">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3 py-20 text-center text-gray-400 text-sm rounded-2xl border border-dashed border-gray-200 bg-white">
            No programs yet. Click <strong>New program</strong> to add one.
          </div>
        )}
      </div>

      {/* ── Add / Edit modal ── */}
      {modal && (
        <AdminModal
          onClose={closeModal}
          title={modal === "add" ? "New program" : "Edit program"}
          testId="program-modal"
          footer={
            <>
              <button onClick={closeModal} className="h-11 px-5 rounded-full text-sm font-medium border border-gray-200 text-gray-600 w-full sm:w-auto">
                Cancel
              </button>
              <button onClick={save} disabled={saving} data-testid="prog-save"
                className="h-11 px-5 rounded-lg bg-[#0F172A] text-white text-sm font-medium w-full sm:w-auto disabled:opacity-50 hover:bg-[#1e293b]">
                {saving ? "Saving…" : modal === "add" ? "Add program" : "Save changes"}
              </button>
            </>
          }
        >
          <div className="grid gap-3">

            {/* Cover image */}
            <div>
              <label className="text-xs font-medium text-gray-500 block mb-1.5">Cover image</label>
              {imgSrc && (
                <div className="relative mb-2 rounded-xl overflow-hidden aspect-[16/7] bg-gray-100">
                  <img src={imgSrc} alt="Cover preview" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setF((prev) => ({ ...prev, cover_image: "" }))}
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white grid place-items-center hover:bg-black/70 transition-colors"
                    title="Remove image"
                    data-testid="remove-cover-image"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <ImageUpload
                value={f.cover_image}
                onChange={(url) => setF((prev) => ({ ...prev, cover_image: url }))}
                testid="prog-cover-image"
              />
              <p className="text-[11px] text-gray-400 mt-1">Upload an image or paste a URL. Recommended: 16:10 ratio.</p>
            </div>

            <input placeholder="Title *" value={f.title}
              onChange={(e) => setF({ ...f, title: e.target.value })}
              data-testid="prog-title" className={SI} />

            <input placeholder="Subtitle" value={f.subtitle}
              onChange={(e) => setF({ ...f, subtitle: e.target.value })} className={SI} />

            <textarea placeholder="Description" value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })}
              className={`${SI} min-h-24 resize-y`} />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input placeholder="Category" value={f.category}
                onChange={(e) => setF({ ...f, category: e.target.value })} className={SI} />
              <input placeholder="Duration hrs" type="number" value={f.duration_hours}
                onChange={(e) => setF({ ...f, duration_hours: e.target.value })} className={SI} />
              <input placeholder="Level" value={f.level}
                onChange={(e) => setF({ ...f, level: e.target.value })} className={SI} />
            </div>

            {/* ── Mode + Published ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-gray-500 font-medium block mb-1">Delivery Mode</label>
                <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}
                  className={SI} data-testid="prog-mode">
                  {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="flex items-end pb-2.5">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={f.is_published}
                    onChange={(e) => setF({ ...f, is_published: e.target.checked })}
                    className="w-4 h-4 accent-[#4F46E5]"
                    data-testid="prog-published"
                  />
                  Published (visible to public)
                </label>
              </div>
            </div>

            {/* Training schedule */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="text-[11px] text-gray-500 font-medium block mb-1">Start Time</label>
                <input type="time" value={f.training_start_time || ""}
                  onChange={(e) => setF({ ...f, training_start_time: e.target.value })} className={SI} />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 font-medium block mb-1">End Time</label>
                <input type="time" value={f.training_end_time || ""}
                  onChange={(e) => setF({ ...f, training_end_time: e.target.value })} className={SI} />
              </div>
              <div>
                <label className="text-[11px] text-gray-500 font-medium block mb-1">Timezone</label>
                <select value={f.timezone || "IST"} onChange={(e) => setF({ ...f, timezone: e.target.value })} className={SI}>
                  <option value="IST">IST (UTC+5:30)</option>
                  <option value="UTC">UTC</option>
                  <option value="UTC+5:30">UTC+5:30</option>
                  <option value="PST">PST (UTC-8)</option>
                  <option value="EST">EST (UTC-5)</option>
                  <option value="GMT">GMT</option>
                  <option value="CET">CET (UTC+1)</option>
                </select>
              </div>
            </div>

            <input placeholder="Venue (optional — e.g. School Auditorium / Online via Zoom)"
              value={f.venue || ""} onChange={(e) => setF({ ...f, venue: e.target.value })} className={SI} />
          </div>
        </AdminModal>
      )}
    </div>
  );
}
